/**
 * PricingRuleCreateSections — composite config sections for the wizard.
 * Each section reads the shared WizardState and mutates it via `set`.
 */

import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { DocTypeSearchAutocomplete } from "@/components/search/DocTypeSearchAutocomplete";
import { CARD } from "@/config/design-tokens";
import {
  Percent,
  DollarSign,
  BadgeDollarSign,
  Package,
  Calendar,
  ArrowRight,
  Truck,
  Sparkles,
  Ticket,
  RefreshCw,
  Plus,
  Trash2,
} from "lucide-react";
import {
  SegmentedControl,
  QuickPicks,
  PriceCalculator,
  ScopeSelector,
  ToggleRow,
} from "./PricingRuleCreateWidgets";
import {
  type WizardState,
  type DiscountMode,
  type ShippingMode,
  type ConditionalCustomization,
  type ConditionalCustomizationAction,
  generateCode,
} from "./pricingRuleWizardModel";
import { StorefrontScopePicker } from "./StorefrontScopePicker";

type SetFn = <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
type T = (k: string) => string;

interface SectionProps {
  s: WizardState;
  set: SetFn;
  lang: string;
  t: T;
}

// ── Discount config ─────────────────────────────────────────

export function DiscountConfig({ s, set, lang }: SectionProps) {
  const modes: { value: DiscountMode; label: string; icon: typeof Percent }[] = [
    { value: "percentage", label: lang === "th" ? "เปอร์เซ็นต์" : "Percentage", icon: Percent },
    { value: "amount", label: lang === "th" ? "จำนวนเงิน" : "Fixed Amount", icon: DollarSign },
  ];
  // Absolute "Set Price" only makes sense for an automatic rule, not a coupon.
  if (!s.isCoupon) modes.push({ value: "rate", label: lang === "th" ? "ตั้งราคา" : "Set Price", icon: BadgeDollarSign });

  return (
    <Card className={CARD}>
      <CardContent className="p-5 space-y-5">
        <SegmentedControl<DiscountMode> options={modes} value={s.discountMode} onChange={(v) => set("discountMode", v)} />
        {s.discountMode && (
          <>
            {s.discountMode !== "rate" && (
              <QuickPicks mode={s.discountMode} value={s.discountValue} onChange={(v) => set("discountValue", v)} />
            )}
            <div className="rounded bg-primary/[0.04] p-5">
              <div className="flex items-center justify-center gap-1 py-1">
                {s.discountMode !== "percentage" && <span className="text-2xl font-light text-muted-foreground">฿</span>}
                <input type="number" value={s.discountValue || ""} onChange={(e) => {
                  let v = parseFloat(e.target.value) || 0;
                  if (s.discountMode === "percentage") v = Math.min(v, 100);
                  set("discountValue", v);
                }} placeholder="0"
                  className={cn(
                    "text-4xl font-semibold tabular-nums bg-transparent border-none outline-none text-center w-[180px]",
                    "placeholder:text-muted-foreground/30",
                    "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                    s.discountValue > 0 ? "text-primary" : "text-foreground"
                  )} />
                {s.discountMode === "percentage" && <span className="text-2xl font-light text-muted-foreground">%</span>}
              </div>
              {s.discountMode === "percentage" && (
                <div className="px-2 mt-2">
                  <Slider value={[s.discountValue]} onValueChange={([v]) => set("discountValue", v)} max={100} step={1} className="w-full" />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-muted-foreground">0%</span>
                    <span className="text-xs text-muted-foreground">50%</span>
                    <span className="text-xs text-muted-foreground">100%</span>
                  </div>
                </div>
              )}
            </div>
            <PriceCalculator mode={s.discountMode} value={s.discountValue} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Free item config ────────────────────────────────────────

export function FreeItemConfig({ s, set, lang, t }: SectionProps) {
  return (
    <Card className={CARD}>
      <CardContent className="p-5 space-y-5">
        <ToggleRow icon={Package} title={t("same_item")} desc={t("same_item_desc")} checked={s.sameItem} onChange={(v) => set("sameItem", v)} />
        {!s.sameItem && (
          <div>
            <label className="text-sm text-muted-foreground mb-1.5 block">{t("free_item")}</label>
            <DocTypeSearchAutocomplete doctype="Item" value={s.freeItem} onChange={(val) => set("freeItem", val)}
              placeholder={lang === "th" ? "ค้นหาสินค้า..." : "Search for an item..."} showInitialSuggestions minSearchLength={0} />
          </div>
        )}
        <div className="rounded bg-primary/[0.04] p-5 text-center">
          <p className="text-sm text-muted-foreground mb-3">{t("free_qty")}</p>
          <div className="flex items-center justify-center gap-4">
            <Button variant="outline" size="icon" className="h-10 w-10 rounded" onClick={() => set("freeQty", Math.max(1, s.freeQty - 1))}>-</Button>
            <span className="text-3xl font-semibold tabular-nums w-16 text-center text-primary">{s.freeQty}</span>
            <Button variant="outline" size="icon" className="h-10 w-10 rounded" onClick={() => set("freeQty", s.freeQty + 1)}>+</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Scope ───────────────────────────────────────────────────

export function ScopeSection({ s, set, lang }: SectionProps) {
  return (
    <Card className={CARD}>
      <CardContent className="p-0">
        <ScopeSelector value={s.applyOn} onChange={(v) => { set("applyOn", v); set("scopeValue", ""); }} lang={lang} />
        {s.applyOn && s.applyOn !== "Transaction" && (
          <div className="px-4 pb-4">
            <DocTypeSearchAutocomplete
              doctype={s.applyOn === "Item Code" ? "Item" : s.applyOn === "Item Group" ? "Item Group" : "Brand"}
              value={s.scopeValue}
              onChange={(val) => set("scopeValue", val)}
              placeholder={s.applyOn === "Item Code" ? (lang === "th" ? "ค้นหาสินค้า..." : "Search item...") : s.applyOn === "Item Group" ? (lang === "th" ? "ค้นหาหมวดหมู่..." : "Search category...") : (lang === "th" ? "ค้นหาแบรนด์..." : "Search brand...")}
              showInitialSuggestions
              minSearchLength={0}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Perks (free shipping / embroidery) ──────────────────────

export function PerksSection({ s, set, lang, t }: SectionProps) {
  const shippingModes: { value: Exclude<ShippingMode, null>; label: string }[] = [
    { value: "free", label: t("shipping_free") },
    { value: "amount", label: t("shipping_amount") },
    { value: "percentage", label: t("shipping_percentage") },
  ];
  return (
    <Card className={CARD}>
      <CardContent className="p-5 space-y-4">
        <ToggleRow icon={Truck} title={t("free_shipping")} desc={t("free_shipping_desc")}
          checked={!!s.freeShipping} onChange={(v) => set("freeShipping", v ? "free" : null)} />
        {s.freeShipping && (
          <div className="pl-1 space-y-3">
            <SegmentedControl<Exclude<ShippingMode, null>>
              options={shippingModes}
              value={s.freeShipping}
              onChange={(v) => set("freeShipping", v)}
            />
            {s.freeShipping !== "free" && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{s.freeShipping === "percentage" ? "%" : "฿"}</span>
                <Input type="number" value={s.shippingValue || ""} onChange={(e) => set("shippingValue", parseFloat(e.target.value) || 0)}
                  placeholder={lang === "th" ? "มูลค่าส่วนลดค่าส่ง" : "Shipping discount value"} className="h-9" />
              </div>
            )}
          </div>
        )}
        <ToggleRow icon={Sparkles} title={t("free_embroidery")} desc={t("free_embroidery_desc")}
          checked={s.freeEmbroidery} onChange={(v) => set("freeEmbroidery", v)} />
      </CardContent>
    </Card>
  );
}

// ── Conditions (min qty / min amt / dates / company) ────────

export function ConditionsSection({ s, set, lang, t }: SectionProps) {
  return (
    <Card className={CARD}>
      <CardContent className="p-0">
        <div className="flex items-start justify-between px-4 py-4 gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium">{t("min_qty")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t("min_qty_hint")}</p>
          </div>
          <input type="number" value={s.minQty} onChange={(e) => set("minQty", e.target.value)} placeholder="Any"
            className="w-20 shrink-0 mt-0.5 text-sm text-right text-muted-foreground bg-transparent border-none outline-none placeholder:text-muted-foreground/40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
        </div>
        <div className="border-t border-border/50 mx-4" />
        <div className="flex items-start justify-between px-4 py-4 gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium">{t("min_amt")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t("min_amt_hint")}</p>
          </div>
          <input type="number" value={s.minAmt} onChange={(e) => set("minAmt", e.target.value)} placeholder="Any"
            className="w-24 shrink-0 mt-0.5 text-sm text-right text-muted-foreground bg-transparent border-none outline-none placeholder:text-muted-foreground/40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
        </div>
        <div className="border-t border-border/50 mx-4" />
        <div className="px-4 py-4">
          <span className="text-sm font-medium">{t("valid_dates")}</span>
          <p className="text-xs text-muted-foreground mt-0.5">{t("valid_dates_hint")}</p>
          <div className="flex items-center mt-2.5 rounded border border-border/60 overflow-hidden">
            <div className="flex-1 flex items-center gap-2 px-3 py-2.5">
              <Calendar className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <input type="datetime-local" value={s.validFrom} onChange={(e) => set("validFrom", e.target.value)}
                className="text-sm text-foreground bg-transparent border-none outline-none w-full [color-scheme:light] dark:[color-scheme:dark]" />
            </div>
            <div className="w-px h-5 bg-border/60" />
            <div className="flex-1 flex items-center gap-2 px-3 py-2.5">
              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <input type="datetime-local" value={s.validUpto} onChange={(e) => set("validUpto", e.target.value)}
                className="text-sm text-foreground bg-transparent border-none outline-none w-full [color-scheme:light] dark:[color-scheme:dark]" />
            </div>
          </div>
        </div>
        <div className="border-t border-border/50 mx-4" />
        <div className="px-4 py-4">
          <span className="text-sm font-medium">{t("company")}</span>
          <p className="text-xs text-muted-foreground mt-0.5">{t("company_hint")}</p>
          <div className="mt-2">
            <DocTypeSearchAutocomplete doctype="Company" value={s.company} onChange={(val) => set("company", val)}
              placeholder={lang === "th" ? "เลือกบริษัท..." : "Select company..."} showInitialSuggestions minSearchLength={0} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Coupon section (the "make it a coupon" toggle + fields) ──

export function CouponSection({ s, set, lang, t }: SectionProps) {
  return (
    <Card className={cn(CARD, s.isCoupon && "ring-1 ring-primary/20")}>
      <CardContent className="p-6 space-y-6">
        <ToggleRow icon={Ticket} title={t("make_coupon")} desc={t("make_coupon_desc")}
          checked={s.isCoupon} onChange={(v) => {
            set("isCoupon", v);
            if (v && !s.couponCode) set("couponCode", generateCode(s.title));
            // Coupons can't use absolute Set Price — fall back to percentage
            if (v && s.discountMode === "rate") set("discountMode", "percentage");
          }} />
        {s.isCoupon && (
          <div className="space-y-7 pt-2">
            {/* Code + generate */}
            <div>
              <label className="text-sm font-medium block">{t("code_label")}</label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">{t("code_hint")}</p>
              <div className="flex items-center gap-2.5">
                <Input value={s.couponCode} onChange={(e) => set("couponCode", e.target.value.toUpperCase())}
                  placeholder="SUMMER25" className="h-11 font-mono tracking-wide uppercase" />
                <Button type="button" variant="outline" className="h-11 gap-1.5 shrink-0"
                  onClick={() => set("couponCode", generateCode(s.title))}>
                  <RefreshCw className="w-3.5 h-3.5" />
                  {t("code_generate")}
                </Button>
              </div>
            </div>

            {/* Public / exclusive */}
            <div className="flex items-center justify-between rounded bg-muted/30 px-5 py-4">
              <div>
                <p className="text-sm font-medium">{t("public_label")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("public_desc")}</p>
              </div>
              <Switch checked={s.couponPublic} onCheckedChange={(v) => set("couponPublic", v)} />
            </div>

            {/* Customer group (targeted) */}
            <div>
              <label className="text-sm font-medium block">{t("customer_group")}</label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">{t("customer_group_hint")}</p>
              <DocTypeSearchAutocomplete doctype="Customer Group" value={s.customerGroup} onChange={(val) => set("customerGroup", val)}
                placeholder={lang === "th" ? "ทุกลูกค้า (เว้นว่าง)" : "All customers (leave empty)"} showInitialSuggestions minSearchLength={0} />
            </div>

            {/* Usage caps */}
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium block">{t("max_use")}</label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">{t("max_use_hint")}</p>
                <Input type="number" value={s.maxUse} onChange={(e) => set("maxUse", e.target.value)} placeholder={lang === "th" ? "ไม่จำกัด" : "Unlimited"} className="h-11" />
              </div>
              <div>
                <label className="text-sm font-medium block">{t("max_collections")}</label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">{t("max_collections_hint")}</p>
                <Input type="number" value={s.maxCollections} onChange={(e) => set("maxCollections", e.target.value)} placeholder={lang === "th" ? "ไม่จำกัด" : "Unlimited"} className="h-11" />
              </div>
            </div>

            {/* Storefront scope — multi-select store picker (hidden for single-store tenants) */}
            <StorefrontScopePicker value={s.storefrontScope} onChange={(v) => set("storefrontScope", v)} t={t} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Conditional customization (Group / Bulk Deal) ──────────
//
// Coupon-gated customization requirements: when this coupon applies, force
// or free a product customization slot (e.g. require + free logo embroidery
// on group orders). Emitted as `conditional_customizations` on the Pricing Rule.

export function ConditionalCustomizationSection({ s, set, lang, t }: SectionProps) {
  const rows = s.conditionalCustomizations;

  const update = (next: ConditionalCustomization[]) => set("conditionalCustomizations", next);
  const patchRow = (i: number, patch: Partial<ConditionalCustomization>) =>
    update(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => update([...rows, { template: "", field: "", action: "require_free" }]);
  const removeRow = (i: number) => update(rows.filter((_, idx) => idx !== i));

  const actionOptions: { value: ConditionalCustomizationAction; label: string }[] = [
    { value: "require", label: t("conditional_action_require") },
    { value: "free", label: t("conditional_action_free") },
    { value: "require_free", label: t("conditional_action_require_free") },
  ];

  return (
    <Card className={CARD}>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center h-9 w-9 rounded bg-muted text-muted-foreground shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium">{t("conditional_header")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t("conditional_desc")}</p>
          </div>
        </div>

        {rows.map((row, i) => (
          <div key={i} className="rounded border border-border/60 p-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">{t("conditional_template")}</label>
                <Input value={row.template} onChange={(e) => patchRow(i, { template: e.target.value })}
                  placeholder={lang === "th" ? "ชื่อเทมเพลต" : "Template name"} className="h-9" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">{t("conditional_field")}</label>
                <Input value={row.field} onChange={(e) => patchRow(i, { field: e.target.value })}
                  placeholder={t("conditional_field_ph")} className="h-9" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">{t("conditional_action")}</label>
              <SegmentedControl<ConditionalCustomizationAction>
                options={actionOptions}
                value={row.action}
                onChange={(v) => patchRow(i, { action: v })}
              />
            </div>
            <button type="button" onClick={() => removeRow(i)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
              {t("conditional_remove")}
            </button>
          </div>
        ))}

        <Button type="button" variant="outline" className="w-full h-10 gap-1.5" onClick={addRow}>
          <Plus className="w-4 h-4" />
          {t("conditional_add")}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Storefront visibility (automatic rule only) ─────────────

export function StorefrontVisibilitySection({ s, set, t }: SectionProps) {
  return (
    <Card className={CARD}>
      <CardContent className="p-0">
        <div className="flex items-center justify-between px-4 py-4">
          <span className="text-sm font-medium">{t("checkout_toggle")}</span>
          <Switch checked={s.storefrontEnabled} onCheckedChange={(v) => set("storefrontEnabled", v)} />
        </div>
        <div className="border-t border-border/50 mx-4" />
        <div className="flex items-center justify-between px-4 py-4">
          <span className="text-sm font-medium">{t("detail_toggle")}</span>
          <Switch checked={s.storefrontShowOnDetail} onCheckedChange={(v) => set("storefrontShowOnDetail", v)} />
        </div>
        {/* Per-store scope — multi-select store picker (hidden for single-store tenants) */}
        <StorefrontScopePicker value={s.storefrontScope} onChange={(v) => set("storefrontScope", v)} t={t} withDivider />
      </CardContent>
    </Card>
  );
}
